import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  getCountFromServer,
  where,
  limit,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { COLLECTIONS } from "@/constants/firestoreCollections";
import type { FinancingRequestDoc } from "@/types/financingRequest";

export async function getFinancingRequests(): Promise<FinancingRequestDoc[]> {
  const q = query(
    collection(db, COLLECTIONS.FINANCING_REQUESTS),
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as FinancingRequestDoc));
}

export async function updateFinancingStatus(id: string, status: string) {
  const ref = doc(db, COLLECTIONS.FINANCING_REQUESTS, id);
  await updateDoc(ref, {
    status,
    updatedAt: serverTimestamp(),
  });
}

export async function addFinancingNote(
  id: string,
  note: string,
  author: string
) {
  const ref = doc(db, COLLECTIONS.FINANCING_REQUESTS, id);
  await updateDoc(ref, {
    adminNotes: arrayUnion({
      note,
      author,
      createdAt: new Date().toISOString(),
    }),
    updatedAt: serverTimestamp(),
  });
}

export async function approveFinancing(id: string, approvedBy: string) {
  const ref = doc(db, COLLECTIONS.FINANCING_REQUESTS, id);
  await updateDoc(ref, {
    status: "aprobado",
    approvedAt: serverTimestamp(),
    approvedBy,
    updatedAt: serverTimestamp(),
  });
}

export async function rejectFinancing(id: string, reason: string) {
  const ref = doc(db, COLLECTIONS.FINANCING_REQUESTS, id);
  await updateDoc(ref, {
    status: "rechazado",
    rejectionReason: reason,
    updatedAt: serverTimestamp(),
  });
}

// --- Panel optimizado ---

export async function getPendingFinancingCount(): Promise<number> {
  const q = query(
    collection(db, COLLECTIONS.FINANCING_REQUESTS),
    where("status", "==", "pendiente")
  );
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
}

export async function getFinancingByStatus(): Promise<{ status: string; count: number }[]> {
  const snapshot = await getDocs(collection(db, COLLECTIONS.FINANCING_REQUESTS));
  const counts: Record<string, number> = {};
  snapshot.docs.forEach((d) => {
    const status = (d.data().status as string) || "pendiente";
    counts[status] = (counts[status] || 0) + 1;
  });
  return Object.entries(counts).map(([status, count]) => ({ status, count }));
}

export async function getRecentFinancingRequests(max = 5): Promise<FinancingRequestDoc[]> {
  const q = query(
    collection(db, COLLECTIONS.FINANCING_REQUESTS),
    orderBy("createdAt", "desc"),
    limit(max)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as FinancingRequestDoc));
}
